import {
	useEffect,
	useRef,
	useState,
} from 'react';
import type { TokaMapInstance } from '@tokamap/core';
import { observer } from 'mobx-react-lite';
import currentProjectEditorStore from '@/TokamapEditor/stores/currentProjectEditorStore';
import type { LastModification } from '@/TokamapEditor/stores/ProjectEditorStore';
import Tooltip from '@widesk-ui/components/Tooltip';
import { lastModificationLabel } from '@/TokamapEditor/ux/lastModification';
import { lastModificationFullDate } from '@/TokamapEditor/ux/lastModification';
import { mapLabel } from '@/TokamapEditor/ux/lastModification';
import { userColor } from '@/TokamapEditor/ux/lastModification';
import { userName } from '@/TokamapEditor/ux/lastModification';

/** `count` = nombre d'"opérations" (formes regroupées en une seule modification + configuration)
 * ayant actuellement au moins une modification en vol — PAS le nombre brut de formes en vol :
 * `@tokamap/core` notifie un statut par forme même pour une modification de groupe (sélection
 * multiple, BESOINS.md §13 — voir `ShapeSaveTracker.trackAndSaveBatch`, qui appelle `notify()` une
 * fois par forme touchée bien qu'un seul appel `onSave` soit envoyé pour tout le groupe), donc
 * compter les formes ferait apparaître "(2)" pour une modification unique appliquée à 2 formes
 * sélectionnées — voir `groupPendingShapeIds` ci-dessous pour comment ces notifications par forme
 * sont regroupées en une seule opération. Pas non plus le nombre brut de requêtes `onSave`/
 * `onSaveConfiguration` envoyées (une même forme, ou la configuration, peut en empiler plusieurs
 * avant résolution), qui n'aurait pas de sens pour un utilisateur final. */
type BannerState = { kind: 'pending'; count: number } | { kind: 'success' | 'error' };

/** Un groupe de formes notifiées "pending" dans le même geste (voir `groupPendingShapeIds`) — vide
 * une fois que toutes ses formes ont réglé (succès ou erreur), à quel moment il ne compte plus dans
 * `pendingCount()`. */
type PendingGroup = { shapeIds: Set<string> };

const AUTO_HIDE_MS = 3000;

function bannerMessage(banner: BannerState): string {
	if (banner.kind === 'pending') {
		return banner.count > 1 ? `Enregistrement en cours… (${banner.count})` : 'Enregistrement en cours…';
	}
	return banner.kind === 'success' ? 'Modifications enregistrées' : "Échec de l'enregistrement — modification annulée";
}

/**
 * Message de statut de sauvegarde (BESOINS.md §7.1), centré dans la TopBar : "Enregistrement en
 * cours… (N)" tant qu'au moins une forme OU la configuration de la carte (vue par défaut — voir
 * `TokaMapInstance.onSaveConfigurationStatusChange`) a une modification en vol — N se met à jour en
 * direct à chaque résolution (succès ou erreur), pas seulement une fois que plus rien n'est en vol
 * — puis un message de succès/erreur qui disparaît après quelques secondes. `@tokamap/core`
 * n'agrège jamais lui-même ces deux sources en un seul évènement (voir CLAUDE.md) : cette
 * agrégation "N choses en vol, peu importe leur nature" est une décision d'UI propre à cet exemple.
 *
 * Si plusieurs entités se règlent en même temps, seul le dernier résultat reçu (succès ou erreur)
 * est affiché une fois que plus rien n'est en vol — un compromis délibéré pour rester un message
 * unique plutôt qu'une liste.
 *
 * Au repos, il affiche la dernière modification connue du PROJET — la plus récente toutes cartes
 * confondues, pas celle de la carte ouverte : cette barre ne parle que du projet. Elle ne vient donc
 * pas que d'ici (voir `ProjectEditorStore.lastModification`) : le projet est collaboratif, ce message
 * doit aussi bouger quand un autre utilisateur écrit (évènements Mercure), et être déjà renseigné au
 * chargement de la page à partir de ce que l'API sait déjà.
 */
const SaveStatusBanner = observer(({ instance }: { instance: TokaMapInstance | null }) => {
	const [banner, setBanner] = useState<BannerState | null>(null);
	// Groupes de formes "pending" en vol (voir `PendingGroup`) — un groupe par opération, pas par
	// forme. `shapeIdToGroup` retrouve le groupe d'une forme au moment où elle règle (succès/erreur),
	// `openGroup` est le groupe encore ouvert à de nouvelles formes (voir son commentaire ci-dessous).
	const pendingGroups = useRef(new Set<PendingGroup>());
	const shapeIdToGroup = useRef(new Map<string, PendingGroup>());
	const openGroup = useRef<PendingGroup | null>(null);
	const configurationPending = useRef(false);
	const hideTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

	useEffect(() => {
		if (!instance) return;

		function pendingCount(): number {
			return pendingGroups.current.size + (configurationPending.current ? 1 : 0);
		}

		// Regroupe les notifications "pending" par forme d'une même modification de groupe (sélection
		// multiple) en une seule opération : `ShapeSaveTracker.trackAndSaveBatch` notifie chaque forme
		// touchée SYNCHRONE MENT, dans la même boucle `for`, avant de rendre la main (voir son
		// commentaire) — donc toutes les formes d'un même geste arrivent ici avant le prochain
		// microtask. `openGroup` reste ouvert à de nouvelles formes jusqu'à ce microtask, qui le
		// referme : une notification "pending" ultérieure (un geste VRAIMENT distinct, forcément séparé
		// par au moins un tick d'évènement DOM réel) ouvre alors un nouveau groupe plutôt que de
		// rejoindre celui-ci.
		function groupPendingShapeId(shapeId: string): void {
			// Déjà en vol : la forme reste dans son groupe. `ShapeSaveTracker` notifie "pending" à chaque
			// nouvel envoi mais ne règle qu'une fois, quand plus rien n'est en vol pour elle — la ranger
			// dans un second groupe laissait le premier ouvert pour toujours, et la barre restait sur
			// « Enregistrement en cours » (une liaison enchaîne plusieurs envois de la même forme).
			if (shapeIdToGroup.current.has(shapeId)) return;

			let group = openGroup.current;
			if (!group) {
				group = { shapeIds: new Set() };
				openGroup.current = group;
				pendingGroups.current.add(group);
				queueMicrotask(() => {
					if (openGroup.current === group) openGroup.current = null;
				});
			}
			group.shapeIds.add(shapeId);
			shapeIdToGroup.current.set(shapeId, group);
		}

		// Une forme réglée (succès/erreur) retire son groupe de `pendingGroups` une fois que TOUTES
		// les formes de ce groupe ont réglé — pas dès la première, sans quoi une modification de
		// groupe encore partiellement en vol disparaîtrait du compte prématurément.
		function settlePendingShapeId(shapeId: string): void {
			const group = shapeIdToGroup.current.get(shapeId);
			if (!group) return;
			shapeIdToGroup.current.delete(shapeId);
			group.shapeIds.delete(shapeId);
			if (group.shapeIds.size === 0) pendingGroups.current.delete(group);
		}

		// Partagé par les deux sources : une fois l'une d'elles réglée (succès/erreur), reflète soit
		// le compte restant en vol (l'autre source, ou une autre forme), soit le résultat final.
		function handleSettled(status: 'success' | 'error'): void {
			const remaining = pendingCount();
			if (remaining > 0) {
				setBanner({ kind: 'pending', count: remaining });
				return;
			}

			setBanner({ kind: status });
			if (status === 'success') {
				currentProjectEditorStore.get?.setLastModification({
					at: new Date(),
					mapUrn: currentProjectEditorStore.get.currentMap.urn,
				});
			}
			hideTimeout.current = setTimeout(() => setBanner(null), AUTO_HIDE_MS);
		}

		const unsubscribeShape = instance.onSaveStatusChange((event) => {
			clearTimeout(hideTimeout.current);

			if (event.status === 'pending') {
				groupPendingShapeId(event.shapeId);
				setBanner({ kind: 'pending', count: pendingCount() });
				return;
			}

			settlePendingShapeId(event.shapeId);
			handleSettled(event.status);
		});

		const unsubscribeConfiguration = instance.onSaveConfigurationStatusChange((event) => {
			clearTimeout(hideTimeout.current);

			if (event.status === 'pending') {
				configurationPending.current = true;
				setBanner({ kind: 'pending', count: pendingCount() });
				return;
			}

			configurationPending.current = false;
			handleSettled(event.status);
		});

		return () => {
			unsubscribeShape();
			unsubscribeConfiguration();
		};
	}, [instance]);

	useEffect(() => () => clearTimeout(hideTimeout.current), []);

	if (!banner) {
		const lastModification = currentProjectEditorStore.get?.lastModification;
		if (!lastModification) return null;

		return (
			<Tooltip title={lastModificationDetail(lastModification)} placement="bottom">
				<div className="save-status-banner save-status-banner--idle">
					{!!lastModification.byUserUrn && (
						<span
							className="save-status-banner__dot"
							style={{ background: userColor(lastModification.byUserUrn) }}
						/>
					)}

					{lastModificationLabel(lastModification.at)}
				</div>
			</Tooltip>
		);
	}

	return (
		<div className={`save-status-banner save-status-banner--${banner.kind}`}>
			<span className="save-status-banner__dot" />

			{bannerMessage(banner)}
		</div>
	);
});

/**
 * Le détail complet, au survol.
 *
 * Trois lignes courtes plutôt qu'une phrase : l'infobulle d'AntD plafonne à 250px et coupait la
 * phrase n'importe où, au milieu d'une date ou d'un nom. Découper nous-même met la césure là où elle
 * a du sens, et un nom de carte à rallonge ne déforme plus que sa propre ligne. (Le composant
 * `Tooltip` de widesk n'expose ni classe ni style sur l'overlay : cette largeur n'est pas réglable
 * d'ici sans passer par un sélecteur `:has` sur le DOM d'AntD.)
 *
 * C'est ici, et pas dans le libellé, que les informations incertaines sont tolérables : un nom qui se
 * précise une fraction de seconde après l'affichage (le temps que l'utilisateur soit résolu) ne se
 * voit pas dans une infobulle fermée, alors qu'il faisait "sauter" le message dans la barre.
 */
function lastModificationDetail(lastModification: LastModification) {
	const where = mapLabel(lastModification.mapUrn);
	const who = lastModification.byUserUrn
		? userName(lastModification.byUserUrn) || 'un autre utilisateur'
		: 'vous';

	return (
		<>
			{!!where && <div style={{ fontWeight: 600 }}>{where}</div>}

			<div>Modifié {lastModificationFullDate(lastModification.at)}</div>

			<div style={{ opacity: 0.75 }}>par {who}</div>
		</>
	);
}

export default SaveStatusBanner;
